import { AlertCircle, Lock } from "lucide-react";

import { Button } from "@/common/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/common/ui/dialog";

import { diffProfileFields, type FieldChange, formatFieldValue } from "./diff-fields";
import type { ProfileRevision } from "./types";
import { useProfileRevisions } from "./use-profile-revisions";

const CHANGE_LABEL: Record<FieldChange, string> = {
  added: "Added",
  changed: "Changed",
  removed: "Removed",
};

const timestampFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "medium",
});

export interface ProfileRevisionsDialogProps {
  userId: string;
  /** Email or display name of the subject, for the dialog heading. */
  userLabel: string;
  onClose: () => void;
}

/**
 * The Ops Console revision view (ENR-1363, design screen 6).
 *
 * Read only, with no authoring affordance anywhere in it: only the user writes
 * their own words (PRD OD-8), and the log itself is append-only in the database.
 * The note at the foot says so, because an operator looking at a history needs
 * to know it is the record and not a working copy.
 */
export function ProfileRevisionsDialog({
  userId,
  userLabel,
  onClose,
}: ProfileRevisionsDialogProps): React.ReactElement {
  const { revisions, total, isLoading, error, reload } = useProfileRevisions(userId);

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="max-w-3xl" data-testid="profile-revisions-dialog">
        <DialogHeader>
          <DialogTitle>Revision history</DialogTitle>
          <DialogDescription>
            Every authored change to <strong>{userLabel}</strong>&apos;s personal profile, newest
            first.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div
            data-testid="profile-revisions-error"
            className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
            <Button
              data-testid="profile-revisions-retry"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        )}

        {isLoading && (
          <p data-testid="profile-revisions-loading" className="py-8 text-center text-sm">
            Loading revision history…
          </p>
        )}

        {!isLoading && !error && revisions.length === 0 && (
          <div
            data-testid="profile-revisions-empty"
            className="py-12 text-center text-muted-foreground"
          >
            <p className="text-sm font-medium">No revisions yet</p>
            <p className="text-sm">This user has not authored their profile.</p>
          </div>
        )}

        {revisions.length > 0 && (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {revisions.map((revision) => (
              <RevisionRow key={revision.id} revision={revision} />
            ))}
          </div>
        )}

        {revisions.length > 0 && (
          <p
            data-testid="profile-revisions-count"
            className="text-xs text-muted-foreground"
          >{`Showing ${revisions.length} of ${total}`}</p>
        )}

        <p
          data-testid="profile-revisions-append-only-note"
          className="flex items-center gap-2 text-xs text-muted-foreground"
        >
          <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
          Append-only. Ops Console cannot edit or delete an entry and never authors a profile.
          Entries older than a year are pruned, except the latest for each profile and any submitted
          to a gym, and erasure removes all of a person&apos;s entries.
        </p>

        <DialogFooter>
          <Button data-testid="profile-revisions-close" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A null `before_fields` is either the first change or one whose earlier state
 * the retention prune removed (ENR-4953); the row cannot tell which, so it
 * names both and never calls the values "added".
 */
const NO_PRIOR_STATE_LABEL = "Recorded";

function RevisionRow({ revision }: { revision: ProfileRevision }): React.ReactElement {
  const entries = diffProfileFields(revision.before_fields, revision.after_fields);
  const noPriorState = revision.before_fields === null;

  return (
    <div
      data-testid={`profile-revision-row-${revision.id}`}
      className="rounded-lg border p-3 text-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-medium">
          {revision.actor_display_name ?? revision.actor_email ?? revision.actor_user_id}
        </span>
        <span className="text-xs text-muted-foreground">
          {timestampFormatter.format(new Date(revision.created_at))}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {revision.actor_email ?? revision.actor_user_id}
        {" · "}
        {revision.scope_kind}
      </p>

      {noPriorState && (
        <p
          data-testid={`profile-revision-no-prior-state-${revision.id}`}
          className="mt-2 text-xs text-muted-foreground"
        >
          No earlier state recorded: this is the first change, or its earlier state was removed
          after a year.
        </p>
      )}

      {entries.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">No field values changed.</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {entries.map((entry) => (
            <li key={entry.path} className="space-y-0.5">
              <span className="font-mono text-xs">{entry.path}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {noPriorState ? NO_PRIOR_STATE_LABEL : CHANGE_LABEL[entry.change]}
              </span>
              <div className="grid gap-0.5 font-mono text-xs">
                {entry.change !== "added" && (
                  <span className="text-muted-foreground">
                    <span aria-hidden="true">- </span>
                    {formatFieldValue(entry.before)}
                  </span>
                )}
                {entry.change !== "removed" && (
                  <span>
                    <span aria-hidden="true">+ </span>
                    {formatFieldValue(entry.after)}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
